import React, { useMemo, useState } from "react"
import { Box, Stack } from "@mui/material"
import { ViraButton } from "../atoms/ViraButton"
import { ViraCard } from "../atoms/ViraCard"
import { LoadingBar } from "../atoms/LoadingBar"
import { Heading, ViraText } from "../atoms/ViraText"
import { ViraTextField } from "../atoms/ViraTextField"
import { SourceMeta, type SourceMetaItem } from "../molecules/SourceMeta"
import { SectionHeader } from "../molecules/SectionHeader"

export type PrepareState = "initial" | "generating" | "complete"

export type PrepSection = {
  id: string
  title: string
  body: string
}

export type PreparePanelProps = {
  state: PrepareState
  clientName: string
  eyebrow?: string
  title?: string
  cardTitle?: string
  sources?: SourceMetaItem[]
  progressLabel?: string
  progressValue?: number
  sections?: PrepSection[]
  /** Optional slot rendered between story sections and the Ask strip (e.g. mood). */
  signals?: React.ReactNode
  askPlaceholder?: string
  askLoading?: boolean
  askAnswer?: string | null
  onAsk?: (question: string) => void
  onGenerate?: () => void
  onBriefSummary?: () => void
  onRegenerate?: () => void
}

/** Storybook / demo fixtures — not used as production defaults. */
export const DEMO_PREPARE_SOURCES: SourceMetaItem[] = [
  { id: "data", label: "through Sep 6", icon: "note" },
  { id: "note", label: "your Aug 18 note", icon: "mic" },
  { id: "plan", label: "their plan", icon: "plan" },
]

export const DEMO_PREPARE_SECTIONS: PrepSection[] = [
  {
    id: "happened",
    title: "What's happened since Sep 6",
    body: "• Sleep stayed fragmented mid-week\n• Enjoyment dipped after Thursday evening\n• Nudge completion was mixed on urge surfing",
  },
  {
    id: "plan",
    title: "How last session's plan went",
    body: "• Completed 3 of 5 scheduled check-ins\n• Urge surfing practiced twice; skipped Friday",
  },
  {
    id: "cover",
    title: "What to cover today",
    body: "• Check in on sleep consistency\n• Review urge surfing practice\n• Choose one interpersonal effectiveness skill",
  },
  {
    id: "ask",
    title: "Ask Vira about Janice",
    body: "• How did sleep track with mood this week?\n• Which evenings looked loneliest?\n• What plan items slipped most often?",
  },
]

function isAskSection(section: PrepSection): boolean {
  const id = section.id.toLowerCase()
  const title = section.title.toLowerCase()
  return id === "ask" || title.startsWith("ask vira")
}

/** Pull suggested follow-ups from an Ask section body (bullet / numbered lines). */
export function parseAskSuggestions(body: string): string[] {
  return body
    .split(/\n+/)
    .map((line) =>
      line
        .replace(/^[\s•\-*]+/, "")
        .replace(/^\d+[.)]\s*/, "")
        .trim()
    )
    .filter((line) => line.length > 6)
    .slice(0, 5)
}

export function PreparePanel({
  state,
  clientName,
  eyebrow,
  title = "Prepare for today’s session",
  cardTitle = "Two minutes of prep, ready in about 30 seconds",
  sources = [],
  progressLabel = "Writing your prep",
  progressValue,
  sections = [],
  signals,
  askPlaceholder,
  askLoading = false,
  askAnswer = null,
  onAsk,
  onGenerate,
  onBriefSummary,
  onRegenerate,
}: PreparePanelProps) {
  const resolvedEyebrow = eyebrow ?? `${clientName} · Before the session`
  const [askDraft, setAskDraft] = useState("")

  const { storySections, askSection } = useMemo(() => {
    const ask = sections.find(isAskSection) ?? null
    const story = sections.filter((section) => !isAskSection(section))
    return { storySections: story, askSection: ask }
  }, [sections])

  const suggestions = useMemo(
    () => (askSection ? parseAskSuggestions(askSection.body) : []),
    [askSection]
  )

  const submitAsk = (question: string) => {
    const trimmed = question.trim()
    if (!trimmed || !onAsk || askLoading) {
      return
    }
    onAsk(trimmed)
    setAskDraft("")
  }

  return (
    <Box sx={{ maxWidth: { xs: "100%", md: 760 }, width: "100%", mx: "auto" }}>
      <SectionHeader level="page" eyebrow={resolvedEyebrow} title={title} />

      {state === "initial" || state === "generating" ? (
        <ViraCard accentTop padded sx={{ maxWidth: "100%" }}>
          <Heading level="card" sx={{ mb: 1.5 }}>
            {cardTitle}
          </Heading>
          <SourceMeta items={sources} />
          <Box sx={{ mt: 2.5 }}>
            {state === "generating" ? (
              <LoadingBar label={progressLabel} value={progressValue} />
            ) : (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <ViraButton onClick={onGenerate} sx={{ minHeight: 40 }}>
                  Generate session prep
                </ViraButton>
                <ViraButton
                  variant="secondary"
                  onClick={onBriefSummary}
                  sx={{ minHeight: 40 }}
                >
                  Brief case summary
                </ViraButton>
              </Stack>
            )}
          </Box>
        </ViraCard>
      ) : null}

      {state === "complete" ? (
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <ViraText
                sx={{
                  display: "block",
                  fontSize: 14,
                  fontWeight: 700,
                  mb: 0.75,
                }}
              >
                Ready for today’s session
              </ViraText>
              <SourceMeta items={sources} />
            </Box>
            <ViraButton
              variant="ghost"
              onClick={onRegenerate}
              sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
            >
              Regenerate
            </ViraButton>
          </Stack>

          <Stack spacing={2}>
            {storySections.length === 0 ? (
              <ViraCard accentTop sx={{ p: 2.5 }}>
                <Heading
                  level="card"
                  sx={{ mb: 1, fontSize: 18, fontWeight: 700 }}
                >
                  Not enough context yet
                </Heading>
                <ViraText
                  tone="muted"
                  sx={{ fontSize: 14.5, lineHeight: 1.65 }}
                >
                  Prep needs client data, a prior note, or an active plan.
                  Generate again after those are available, or use Ask Vira with
                  a specific question.
                </ViraText>
              </ViraCard>
            ) : (
              storySections.map((section, index) => (
                <ViraCard
                  key={section.id}
                  accentTop={index === 0}
                  sx={{ p: 2.5 }}
                >
                  <Heading
                    level="card"
                    sx={{
                      mb: 1,
                      fontSize: index === 0 ? 18 : 16.5,
                      fontWeight: 700,
                    }}
                  >
                    {section.title}
                  </Heading>
                  <ViraText
                    tone="muted"
                    sx={{
                      fontSize: 14.5,
                      lineHeight: 1.65,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {section.body}
                  </ViraText>
                </ViraCard>
              ))
            )}
          </Stack>

          {signals}

          {askSection || onAsk ? (
            <ViraCard padded sx={{ maxWidth: "100%" }}>
              <Heading
                level="card"
                sx={{ mb: 1, fontSize: 17, fontWeight: 700 }}
              >
                {askSection?.title || `Ask Vira about ${clientName}`}
              </Heading>
              {suggestions.length > 0 ? (
                <Stack spacing={1} sx={{ mb: onAsk ? 2 : 0 }}>
                  {suggestions.map((suggestion) => (
                    <ViraButton
                      key={suggestion}
                      variant="secondary"
                      onClick={() => submitAsk(suggestion)}
                      disabled={!onAsk || askLoading}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        minHeight: 40,
                        height: "auto",
                        py: 1,
                        whiteSpace: "normal",
                      }}
                    >
                      {suggestion}
                    </ViraButton>
                  ))}
                </Stack>
              ) : askSection?.body && !onAsk ? (
                <ViraText
                  tone="muted"
                  sx={{
                    fontSize: 14.5,
                    lineHeight: 1.65,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {askSection.body}
                </ViraText>
              ) : null}

              {onAsk ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "stretch", sm: "flex-start" }}
                >
                  <ViraTextField
                    fullWidth
                    size="small"
                    placeholder={
                      askPlaceholder || `Ask a follow-up about ${clientName}…`
                    }
                    value={askDraft}
                    disabled={askLoading}
                    onChange={(event) => setAskDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault()
                        submitAsk(askDraft)
                      }
                    }}
                  />
                  <ViraButton
                    onClick={() => submitAsk(askDraft)}
                    disabled={askLoading || !askDraft.trim()}
                    sx={{ minHeight: 40, flexShrink: 0 }}
                  >
                    {askLoading ? "Asking…" : "Ask"}
                  </ViraButton>
                </Stack>
              ) : null}

              {askLoading ? (
                <Box sx={{ mt: 2 }}>
                  <LoadingBar label="Thinking" />
                </Box>
              ) : null}

              {askAnswer ? (
                <ViraText
                  tone="muted"
                  sx={{
                    mt: 2,
                    fontSize: 14.5,
                    lineHeight: 1.65,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {askAnswer}
                </ViraText>
              ) : null}
            </ViraCard>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  )
}
